import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useTasks } from '@/hooks/useTasks';
import type { ExtendedTask } from '@/hooks/useTasks';
import { TaskList } from '@/components/crm/TaskList';
import { TaskCalendar } from '@/components/crm/TaskCalendar';
import { AddTaskModal } from '@/components/crm/AddTaskModal';
import { TaskDetail } from '@/components/crm/TaskDetail';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { CRMPageHeader } from '@/components/crm/CRMPageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  List,
  Calendar as CalendarIcon,
  Plus,
  Search,
  Filter,
  Trash2,
  CheckCircle2,
  AlertCircle,
  Clock,
  CalendarDays,
  LayoutGrid,
  CheckSquare,
  UserPlus,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import type { TaskFilter, TaskFormData } from '@/types/tasks';
import { FlowInlineLauncher } from '@/components/flow/FlowWidget';

type ViewMode = 'list' | 'calendar';
type FilterTab = 'all' | 'today' | 'upcoming' | 'overdue' | 'completed';

export function TasksPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTasks, setSelectedTasks] = useState<string[]>([]);
  const [detailTask, setDetailTask] = useState<ExtendedTask | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<ExtendedTask | null>(null);
  const [initialDate, setInitialDate] = useState<Date | undefined>();
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');

  const {
    filteredTasks,
    stats,
    setFilters,
    addTask,
    updateTask,
    deleteTask,
    completeTask,
    reopenTask,
    assignTask,
    checkOverdue,
    assigneeOptions,
  } = useTasks();

  // Check for overdue tasks periodically
  useEffect(() => {
    checkOverdue();
    const interval = setInterval(checkOverdue, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [checkOverdue]);

  // Apply filters when tab changes
  useEffect(() => {
    if (activeTab === 'all') {
      setFilters((prev: TaskFilter) => ({ ...prev, status: undefined }));
    } else {
      setFilters((prev: TaskFilter) => ({ ...prev, status: activeTab }));
    }
  }, [activeTab, setFilters]);

  // Apply search filter
  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setFilters((prev: TaskFilter) => ({ ...prev, search: query || undefined }));
  };

  // Apply priority filter
  const handlePriorityFilter = (priority: string) => {
    setPriorityFilter(priority);
    setFilters((prev: TaskFilter) => ({
      ...prev,
      priority: priority === 'all' ? undefined : priority as TaskFilter['priority'],
    }));
  };

  // Apply assignee filter
  const handleAssigneeFilter = (assignee: string) => {
    setAssigneeFilter(assignee);
    setFilters((prev: TaskFilter) => ({
      ...prev,
      assignedTo: assignee === 'all' ? undefined : assignee,
    }));
  };

  // Selection handlers
  const handleSelectTask = (taskId: string, selected: boolean) => {
    if (selected) {
      setSelectedTasks((prev) => [...prev, taskId]);
    } else {
      setSelectedTasks((prev) => prev.filter((id) => id !== taskId));
    }
  };

  // Task actions
  const handleTaskClick = (task: ExtendedTask) => {
    setDetailTask(task);
    setIsDetailOpen(true);
  };

  const handleAddTask = (date?: Date) => {
    setEditingTask(null);
    setInitialDate(date);
    setIsAddModalOpen(true);
  };

  const handleEditTask = (task: ExtendedTask) => {
    setEditingTask(task);
    setIsAddModalOpen(true);
    setIsDetailOpen(false);
  };

  const handleSaveTask = (taskData: TaskFormData) => {
    if (editingTask) {
      updateTask(editingTask.id, taskData);
      toast.success('Task updated successfully');
    } else {
      addTask({
        ...taskData,
        status: 'todo',
      } as any);
      toast.success('Task created successfully');
    }
    setIsAddModalOpen(false);
    setEditingTask(null);
  };

  const handleCompleteTask = (taskId: string) => {
    completeTask(taskId);
    toast.success('Task completed!');
  };

  const handleReopenTask = (taskId: string) => {
    reopenTask(taskId);
    toast.info('Task reopened');
  };

  const handleDeleteTask = (taskId: string) => {
    deleteTask(taskId);
    setIsDetailOpen(false);
    setSelectedTasks((prev) => prev.filter((id) => id !== taskId));
    toast.success('Task deleted');
  };

  const handleBulkComplete = () => {
    selectedTasks.forEach((id) => completeTask(id));
    setSelectedTasks([]);
    toast.success(`${selectedTasks.length} tasks completed`);
  };

  const handleBulkDelete = () => {
    selectedTasks.forEach((id) => deleteTask(id));
    setSelectedTasks([]);
    toast.success(`${selectedTasks.length} tasks deleted`);
  };

  const handleAssign = (taskId: string, assigneeId: string, assigneeName: string) => {
    assignTask(taskId, assigneeId, assigneeName);
    toast.success('Task assigned');
  };

  // Tab config
  const tabs = [
    { id: 'all', label: 'All', count: stats.total, icon: LayoutGrid },
    { id: 'today', label: 'Today', count: stats.today, icon: Clock },
    { id: 'upcoming', label: 'Upcoming', count: stats.upcoming, icon: CalendarDays },
    { id: 'overdue', label: 'Overdue', count: stats.overdue, icon: AlertCircle },
    { id: 'completed', label: 'Completed', count: stats.completed, icon: CheckSquare },
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <CRMPageHeader
        title="Tasks & Reminders"
        description="Manage follow-ups, overdue reminders, and scheduled work"
        actions={(
          <>
            <div className="flex flex-wrap items-center gap-2">
              <FlowInlineLauncher />
              <div className="flex items-center rounded-lg border bg-muted/40 p-1">
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="gap-2"
                >
                  <List className="h-4 w-4" />
                  List
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('calendar')}
                  className="gap-2"
                >
                  <CalendarIcon className="h-4 w-4" />
                  Calendar
                </Button>
              </div>
            </div>

            <Button 
              onClick={() => handleAddTask()}
              className="bg-[#2B7FFF] hover:bg-[#1A5FCC]"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Task
            </Button>
          </>
        )}
      />

      <div className="rounded-xl border bg-[#2B7FFF]/5 px-4 py-3 text-sm text-muted-foreground">
        This page is your follow-up hub: use it to track next steps, surface overdue reminders, and keep investor outreach moving.
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#2B7FFF]/10 flex items-center justify-center">
              <CheckSquare className="h-5 w-5 text-[#2B7FFF]" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Tasks</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-green-500/10 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-xl font-bold">{stats.completed}</p>
            </div>
          </div>
        </div>
        <div className="rounded-xl border bg-card p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Due Today</p>
              <p className="text-xl font-bold">{stats.today}</p>
            </div>
          </div>
        </div>
        <div className={cn(
          'rounded-xl border bg-card p-4 shadow-sm',
          stats.overdue > 0 && 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/10'
        )}>
          <div className="flex items-center gap-3">
            <div className={cn(
              'h-10 w-10 rounded-xl flex items-center justify-center',
              stats.overdue > 0 ? 'bg-red-500/10' : 'bg-slate-500/10'
            )}>
              <AlertCircle className={cn(
                'h-5 w-5',
                stats.overdue > 0 ? 'text-red-500' : 'text-muted-foreground'
              )} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Overdue</p>
              <p className={cn(
                'text-xl font-bold',
                stats.overdue > 0 && 'text-red-600 dark:text-red-400'
              )}>{stats.overdue}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Tabs */}
      <div className="space-y-3 rounded-2xl border bg-card p-4 shadow-sm">
        {/* Filter Tabs */}
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveTab(tab.id as FilterTab)}
                className={cn(
                  'gap-2 rounded-full px-3',
                  activeTab === tab.id && 'bg-[#2B7FFF] hover:bg-[#1A5FCC]'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                <Badge 
                  variant={activeTab === tab.id ? 'secondary' : 'outline'} 
                  className="ml-1 text-xs"
                >
                  {tab.count}
                </Badge>
              </Button>
            );
          })}
        </div>

        {/* Search & Filter Controls */}
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search follow-ups..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2 xl:justify-end">
            <Select value={priorityFilter} onValueChange={handlePriorityFilter}>
              <SelectTrigger className="w-[150px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>

            <Select value={assigneeFilter} onValueChange={handleAssigneeFilter}>
              <SelectTrigger className="w-[160px]">
                <UserPlus className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Assigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assignees</SelectItem>
                {assigneeOptions.map((assignee: { value: string; label: string }) => (
                  <SelectItem key={assignee.value} value={assignee.value}>
                    {assignee.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedTasks.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-wrap items-center gap-3 rounded-xl border border-[#2B7FFF]/20 bg-[#2B7FFF]/10 p-3"
        >
          <Badge variant="secondary">{selectedTasks.length} selected</Badge>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={handleBulkComplete}>
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Complete
          </Button>
          <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setSelectedTasks([])}>
            <X className="h-4 w-4" />
          </Button>
        </motion.div>
      )}

      {/* Content */}
      <div className="min-h-[500px] rounded-2xl border bg-card p-2 shadow-sm">
        {viewMode === 'list' ? (
          <TaskList
            tasks={filteredTasks}
            onTaskClick={handleTaskClick}
            onComplete={handleCompleteTask}
            onReopen={handleReopenTask}
            onDelete={handleDeleteTask}
            onEdit={handleEditTask}
            selectedTasks={selectedTasks}
            onSelectTask={handleSelectTask}
          />
        ) : (
          <TaskCalendar
            tasks={filteredTasks}
            onTaskClick={handleTaskClick}
            onAddTask={handleAddTask}
            onComplete={handleCompleteTask}
          />
        )}
      </div>

      {/* Detail Panel */}
      <TaskDetail
        task={detailTask}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        onEdit={handleEditTask}
        onDelete={handleDeleteTask}
        onComplete={handleCompleteTask}
        onReopen={handleReopenTask}
        onAssign={handleAssign}
      />

      {/* Add/Edit Modal */}
      <AddTaskModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingTask(null);
          setInitialDate(undefined);
        }}
        onSave={handleSaveTask}
        task={editingTask}
        initialDate={initialDate}
      />
    </div>
  );
}

export default TasksPage;

